import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, SlidersHorizontal, MoreVertical } from 'lucide-react';
import { useCategories, useEstablishments } from '../hooks/useData';
import { getImageUrl } from '../utils/getImageUrl';
import './Categories.css';

const Categories = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const typeForFilter = searchParams.get('type') || 'all';

    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState(typeForFilter);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [panelStyle, setPanelStyle] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 280 });
    const filterBtnRef = useRef<HTMLButtonElement | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);

    // Sync filter when URL changes (e.g. navigation via <Link>)
    useEffect(() => {
        setActiveCategory(typeForFilter);
    }, [typeForFilter]);

    // Fetch data
    const { data: categories } = useCategories();
    const { data: allPlaces } = useEstablishments();

    const activeCategoryLabel = useMemo(() => {
        if (activeCategory === 'all') return 'Todas';
        return categories.find((cat) => cat.slug === activeCategory)?.label || 'Todas';
    }, [activeCategory, categories]);

    useEffect(() => {
        if (!isFilterOpen) return;

        const updatePosition = () => {
            const btn = filterBtnRef.current;
            if (!btn) return;
            const rect = btn.getBoundingClientRect();
            const panelWidth = Math.min(280, window.innerWidth - 16);
            const left = Math.max(8, rect.right - panelWidth);
            setPanelStyle({
                top: rect.bottom + 8,
                left,
                width: panelWidth,
            });
        };

        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node;
            if (panelRef.current?.contains(target) || filterBtnRef.current?.contains(target)) return;
            setIsFilterOpen(false);
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        document.addEventListener('mousedown', handleOutsideClick);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isFilterOpen]);

    const filteredPlaces = useMemo(() => {
        return allPlaces.filter(place => {
            const matchesSearch = place.name.toLowerCase().includes(searchTerm.toLowerCase());
            // Filter by category slug if available, using strict check
            const placeCategorySlug = place.category?.slug;
            const matchesCategory = activeCategory === 'all' || placeCategorySlug === activeCategory;
            return matchesSearch && matchesCategory;
        });
    }, [searchTerm, activeCategory, allPlaces]);

    return (
        <div className="categories-page">
            <div className="explore-top">
                <h1>Explorar</h1>
                <div className="search-row">
                    <div className="search-bar">
                        <Search size={20} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Buscar estabelecimentos..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button
                        ref={filterBtnRef}
                        type="button"
                        className={`filter-btn ${activeCategory !== 'all' ? 'active' : ''}`}
                        aria-label="Filtros"
                        aria-expanded={isFilterOpen}
                        onClick={() => setIsFilterOpen((prev) => !prev)}
                    >
                        <SlidersHorizontal size={20} />
                    </button>
                    {isFilterOpen && (
                        <div
                            ref={panelRef}
                            className="filter-panel"
                            style={{
                                top: `${panelStyle.top}px`,
                                left: `${panelStyle.left}px`,
                                width: `${panelStyle.width}px`,
                            }}
                        >
                            <button
                                type="button"
                                className={`filter-option ${activeCategory === 'all' ? 'selected' : ''}`}
                                onClick={() => {
                                    setActiveCategory('all');
                                    setIsFilterOpen(false);
                                }}
                            >
                                Todas as categorias
                            </button>
                            {categories.map((cat) => (
                                <button
                                    key={cat.id}
                                    type="button"
                                    className={`filter-option ${activeCategory === cat.slug ? 'selected' : ''}`}
                                    onClick={() => {
                                        setActiveCategory(cat.slug);
                                        setIsFilterOpen(false);
                                    }}
                                >
                                    {cat.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                {!isFilterOpen && (
                    <p className="active-filter-hint">
                        Filtro atual: <strong>{activeCategoryLabel}</strong>
                    </p>
                )}
            </div>

            <div className="feed-list">
                {filteredPlaces.length > 0 ? (
                    filteredPlaces.map((place) => {
                        const userHandle = `@${place.name.toLowerCase().replace(/\s+/g, '_')}`;
                        return (
                            <article key={place.id} className="post-card" onClick={() => navigate(`/establishment/${place.id}`)}>
                                <header className="post-header">
                                    <div className="post-profile">
                                        <div className="post-avatar">
                                            {place.name.split(' ').map(word => word[0]).join('').slice(0, 2)}
                                        </div>
                                        <div className="post-author">
                                            <strong>{place.name}</strong>
                                            <span>{userHandle}</span>
                                        </div>
                                    </div>
                                    <button type="button" className="post-more" aria-label="Mais opções">
                                        <MoreVertical size={18} />
                                    </button>
                                </header>

                                <div className="post-media-wrap">
                                    <img src={getImageUrl(place.image)} alt={place.name} className="post-media" />
                                    {place.discount && <span className="post-discount">{place.discount}</span>}
                                </div>
                            </article>
                        );
                    })
                ) : (
                    <div className="no-results">
                        <p>Nenhum estabelecimento encontrado.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Categories;
